import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAndroid, isIOS, isElectronApp, isReactNative } from '../../helpers/common';
import { DesktopAppOverlay, ContentContainer, Title, OpenHereButton } from './styles';
import Image from '../image';

interface OpenDesktopPromptProps {
  className?: string;
  onClickOpenHere?: () => void;
}

const STORAGE_KEY = 'open-desktop-prompt-dismissed';
const SESSION_ATTEMPT_KEY = 'open-desktop-auto-attempted';

const OpenDesktopPrompt: React.FC<OpenDesktopPromptProps> = ({ className, onClickOpenHere }) => {
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [autoOpenAttempted, setAutoOpenAttempted] = useState<boolean>(false);
  const [autoOpenFailed, setAutoOpenFailed] = useState<boolean>(false);
  const [showInterstitial, setShowInterstitial] = useState<boolean>(false);

  useEffect(() => {
    try {
      const val = window.localStorage.getItem(STORAGE_KEY);
      if (val === '1') setDismissed(true);
    } catch {}
  }, []);

  const isMobile = isAndroid() || isIOS();
  const shouldConsider = useMemo(() => {
    if (dismissed) return false;
    if (isElectronApp()) return false;
    if (isReactNative()) return false;
    if (isMobile) return false;
    return true;
  }, [dismissed, isMobile]);

  const deepLink = useMemo(() => {
    try {
      const currentUrl = window.location.href;
      return `teamcamp://open?url=${encodeURIComponent(currentUrl)}`;
    } catch {
      return 'teamcamp://open';
    }
  }, []);

  const tryOpenDesktop = useCallback(() => {
    setShowInterstitial(true);
    let timeoutId: number | undefined;

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (timeoutId) window.clearTimeout(timeoutId);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    timeoutId = window.setTimeout(() => {
      setAutoOpenFailed(true);
      setAutoOpenAttempted(true);
    }, 1500);

    try {
      window.location.href = deepLink;
    } catch {
      if (timeoutId) window.clearTimeout(timeoutId);
      setAutoOpenFailed(true);
      setAutoOpenAttempted(true);
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [deepLink]);

  // Attempt to open desktop app automatically
  useEffect(() => {
    if (!shouldConsider) return;

    const alreadyTriedThisSession = (() => {
      try {
        return sessionStorage.getItem(SESSION_ATTEMPT_KEY) === '1';
      } catch {
        return false;
      }
    })();

    if (!alreadyTriedThisSession) {
      try {
        sessionStorage.setItem(SESSION_ATTEMPT_KEY, '1');
      } catch {}
      tryOpenDesktop();
    }
  }, [shouldConsider, tryOpenDesktop]);

  const onOpenHereInstead = () => {
    setShowInterstitial(false);
    setAutoOpenAttempted(true);
    onClickOpenHere?.();
  };

  // Interstitial page (centered) after we attempt to open the desktop app
  if (shouldConsider && showInterstitial) {
    return (
      <DesktopAppOverlay role='region' aria-label='Desktop app open interstitial'>
        <ContentContainer>
          <Image imageKey='teamcamp_logo' alt='Teamcamp logo' width={96} height={96} />
          <Title>Opened in Teamcamp app</Title>
          <OpenHereButton onClick={onOpenHereInstead}>Open here instead</OpenHereButton>
        </ContentContainer>
      </DesktopAppOverlay>
    );
  }

  // Banner appears only if auto-open failed or user wants the option
  if (!shouldConsider) return null;
  if (!autoOpenAttempted && !autoOpenFailed) return null;

  return <></>;
};

export default OpenDesktopPrompt;
